import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Curated "must-have" notes for the archive.
 *
 * Why this exists: the archive feed is built by paging an author's kind-1
 * history across relays. No relay is obligated to keep an author's full
 * history, so rare / old / important notes can be absent from every relay the
 * client can reach. This module guarantees specific notes always appear:
 *
 *   1. Look each id up by exact id across a broad relay group (usePinnedNotes).
 *   2. If no relay serves it, fall back to an EMBEDDED copy defined here.
 *
 * Two kinds of embedded fallback are supported (see PINNED_FALLBACKS):
 *
 *   - `verified: true`  — a byte-for-byte signed copy. The app re-checks its
 *     id + signature and only shows it if they validate. Fully trustless.
 *
 *   - `verified: false` — a hand-entered RECONSTRUCTION used when the exact
 *     signed bytes are unavailable (e.g. the note only survives as rendered
 *     text on another client). It is shown only because the archivist vouches
 *     for it, is constrained to the archive author, and is flagged in the UI
 *     as an unverified reconstruction so viewers know it wasn't signature-
 *     checked. Replace it with a `verified: true` copy if the original bytes
 *     are ever recovered.
 */

/** The archive author's pubkey. */
export const PINNED_LOOKUP_AUTHOR =
  '90152b7f8dffbcfc462f5c02f122f72e3bae58ccb059431367e2790204e57401';

/** Hex ids the archive must always try to surface via relays. */
export const PINNED_NOTE_IDS: string[] = [
  // "THE CURRENT SITUATION IN PALESTINE (ORE 49) - CIA October 20, 1947."
  'c51bf1647da21c93162f9d66e476766ff9e81d7883d15bce73ca88d9cb2d6bc1',
  // "THE CONSEQUENCES OF THE PARTITION OF PALESTINE - CIA November 28, 1947."
  'dc6b295a263eac917927686209b46908ee2e3960ee3a02d71dce01b2c0902673',
  // "King David Hotel Bombing (1946)"
  'a9e7a5d7752eefda4e8b5d313eb497203cb2e34c61e959bacb4428dca490fc93',
  // "Reuters report on Palestinian group DFLP taking credit for 9/11 attacks…"
  '51108abe9b12db1ae75f4a1f0ca7e1c4271297ad98d84f389b665d656caffd26',
];

export interface PinnedFallback {
  /** The event (signed copy, or best-effort reconstruction). */
  event: NostrEvent;
  /**
   * Whether `event` is the exact signed original (id + sig validate). When
   * false, the event is a vouched-for reconstruction and the UI marks it.
   */
  verified: boolean;
}

/**
 * Embedded copies of important events relays may no longer serve.
 */
export const PINNED_FALLBACKS: PinnedFallback[] = [
  {
    verified: false,
    event: {
      id: 'c51bf1647da21c93162f9d66e476766ff9e81d7883d15bce73ca88d9cb2d6bc1',
      pubkey: '90152b7f8dffbcfc462f5c02f122f72e3bae58ccb059431367e2790204e57401',
      created_at: 1743477259,
      kind: 1,
      tags: [
        ['t', 'palestine'],
        ['t', 'israel'],
        ['t', 'zionism'],
        ['t', 'zionist'],
        ['t', 'cia'],
      ],
      content:
        'THE CURRENT SITUATION IN PALESTINE (ORE 49) - CIA October 20, 1947.\n\n#palestine #israel #zionism #zionist #cia\nhttps://m.primal.net/QAFW.png https://m.primal.net/QAFX.png https://m.primal.net/QAFZ.png https://m.primal.net/QAFa.png https://m.primal.net/QAFb.png https://m.primal.net/QAFc.png',
      sig: '72e29f6f8bdb3d2515b6e8210b71010fa88fb1d0f55ec43fa2faa85ea47fa330a38c87e3e0a3922873f5a2ac7f0afb09520290bf3e939537a7f60c04c7e24bab',
    },
  },
  {
    // Exact signed bytes fetched from a relay — passes id + signature checks.
    verified: true,
    event: {
      id: 'dc6b295a263eac917927686209b46908ee2e3960ee3a02d71dce01b2c0902673',
      pubkey: '90152b7f8dffbcfc462f5c02f122f72e3bae58ccb059431367e2790204e57401',
      created_at: 1743479574,
      kind: 1,
      tags: [
        ['t', 'palestine'],
        ['t', 'israel'],
        ['t', 'zionism'],
        ['t', 'zionist'],
        ['t', 'cia'],
        ['r', 'wss://nos.lol/'],
        ['r', 'wss://purplepag.es/'],
        ['r', 'wss://relay.primal.net/'],
        ['r', 'wss://relay.damus.io/'],
        ['r', 'wss://nostr.wine/'],
        ['r', 'wss://atlas.nostr.land/invoices'],
        ['r', 'wss://nostr.bitcoiner.social/'],
        ['r', 'wss://eden.nostr.land/'],
        ['r', 'wss://relay.siamstr.com/'],
        ['r', 'wss://hist.nostr.land/'],
        ['r', 'wss://nostr.mom/'],
        ['r', 'wss://rss.nos.social/'],
        ['r', 'wss://nostr.land/'],
      ],
      content:
        'THE CONSEQUENCES OF THE PARTITION OF PALESTINE - CIA November 28, 1947.\n\n https://m.primal.net/QAGh.png  https://m.primal.net/QAGi.png  https://m.primal.net/QAGj.png  https://m.primal.net/QAGk.png  https://m.primal.net/QAGl.png  https://m.primal.net/QAGn.png  https://m.primal.net/QAGp.png  https://m.primal.net/QAGv.png  https://m.primal.net/QAGx.png  https://m.primal.net/QAGy.png  https://m.primal.net/QAGz.png  https://m.primal.net/QAHA.png  https://m.primal.net/QAHB.png  https://m.primal.net/QAHC.png  https://m.primal.net/QAHD.png  https://m.primal.net/QAHE.png  https://m.primal.net/QAHF.png   https://m.primal.net/QAHI.png \n\n#palestine #israel #zionism #zionist #cia',
      sig: 'e873e0453414c2d07739d049352c4024f36bb0df4b05ae6696a6519a4b96df5af4f74973785ec45f0a5dec0bbaf61372ad18ee4ecf2514f90148f5d41702b814',
    },
  },
  {
    // Exact signed bytes fetched from a relay — passes id + signature checks.
    verified: true,
    event: {
      id: 'a9e7a5d7752eefda4e8b5d313eb497203cb2e34c61e959bacb4428dca490fc93',
      pubkey: '90152b7f8dffbcfc462f5c02f122f72e3bae58ccb059431367e2790204e57401',
      created_at: 1743392586,
      kind: 1,
      tags: [
        ['t', 'palestine'],
        ['t', 'jerusalem'],
        ['t', 'israel'],
        ['t', 'september11'],
        ['t', 'sept11'],
      ],
      content:
        'King David Hotel Bombing (1946)\n\n#palestine #jerusalem #israel #september11 #sept11 \n\nhttps://m.primal.net/PyyT.mp4\n',
      sig: '064ac83d45cab52faffc3d431b89b4ce6675cc4e1ab9cebd6684aa969ca1e34d28267ff88323687fd195e02df4869361d0d34626b368500a4df8f37ce9f73d6c',
    },
  },
  {
    // Exact signed bytes fetched from a relay — passes id + signature checks.
    verified: true,
    event: {
      id: '51108abe9b12db1ae75f4a1f0ca7e1c4271297ad98d84f389b665d656caffd26',
      pubkey: '90152b7f8dffbcfc462f5c02f122f72e3bae58ccb059431367e2790204e57401',
      created_at: 1765402082,
      kind: 1,
      tags: [],
      content:
        'Reuters report on Palestinian group DFLP taking credit for 9/11 attacks as Twin Towers are burning\n\nABC NEWS 9-11-2001  9:40 A.M. \n\n\n\nhttps://blossom.primal.net/094d9a1f861e04a0d2027191b197f98cfa9cd330421aff493c25cc407da154d9.mp4',
      sig: 'fd57cb2b01eef03c389add79c4c60e0024c26c46b2f8d289c11e539bfd29519d5e9f4050b0758a5fb3fc9c8b54e06894b36a764a36846c12bc3907e3633f3358',
    },
  },
];
